import React from 'react';
import { Star, Video, ListChecks, Youtube } from 'lucide-react';
import GphRichBlock from '../GphRichBlock';
import { getDrillById, GPH_DRILLS } from '../../../data/gphDrillCatalog';
import { findCoherentDrill } from '../../../services/gph/gphCoherenceGuard';

// Coach V2: o usuário pede uma jogada, o GPH entrega UM treino prático completo —
// com material de apoio compacto no final, favoritar e CTA para validar com vídeo.
// Coherence Guard: quando o contexto restringe categorias (diagnóstico/situação),
// o drill entregue OBRIGATORIAMENTE pertence a uma categoria permitida.

const Section = ({ label, children }) => (
  <div className="gph-drill-section">
    <h4 className="gph-drill-section__label">{label}</h4>
    {children}
  </div>
);

export default function TrainingSkillAdapter({ agent, context = {} }) {
  let drill;
  if (context.drillId) {
    drill = getDrillById(context.drillId);
  } else {
    const coherent = findCoherentDrill(
      context.drillGoal || context.rawText || '',
      context.allowedCategories || null
    );
    drill = coherent.drill || GPH_DRILLS[0];
  }

  if (!drill) {
    return (
      <GphRichBlock title="Treino específico de jogada">
        <p className="gph-msg__text">Não encontrei um treino para essa jogada ainda. Me diga outra situação.</p>
      </GphRichBlock>
    );
  }

  const isFavorite = agent.favorites.isFavorite(drill.drillId);
  const complementary = drill.supportMaterial?.complementaryDrillId
    ? getDrillById(drill.supportMaterial.complementaryDrillId)
    : null;

  return (
    <GphRichBlock eyebrow="Treino de jogada" title={drill.title} subtitle={drill.scenario}>
      <Section label="Erro que corrige">
        <p>{drill.fixesMistake}</p>
      </Section>

      <Section label="Por que funciona">
        <p>{drill.whyItWorks}</p>
      </Section>

      <Section label="Como executar">
        <ol className="gph-block-list gph-block-list--ordered">
          {drill.howToExecute.map((step, index) => <li key={index}>{step}</li>)}
        </ol>
      </Section>

      <Section label="Erros comuns">
        <ul className="gph-block-list">
          {drill.commonErrors.map((error, index) => <li key={index}>{error}</li>)}
        </ul>
      </Section>

      <Section label="Como treinar">
        <p>{drill.howToTrain}</p>
      </Section>

      <Section label="Como validar">
        <p>{drill.howToValidate}</p>
      </Section>

      <Section label="Material de apoio">
        <div className="gph-support-material">
          {/* Vídeo REAL vindo da pesquisa (Truth Policy: só com URL de provider). */}
          {context.supportMaterial?.url && (
            <div className="gph-support-card">
              <span className="gph-support-card__label"><Youtube size={13} /> Vídeo de apoio</span>
              <a
                href={context.supportMaterial.url}
                target="_blank"
                rel="noopener noreferrer"
                className="gph-link-btn"
              >
                {context.supportMaterial.title}
              </a>
              {context.supportMaterial.channel && <p>Canal: {context.supportMaterial.channel}</p>}
            </div>
          )}
          {drill.supportMaterial?.checklist?.length > 0 && (
            <div className="gph-support-card">
              <span className="gph-support-card__label"><ListChecks size={13} /> Checklist</span>
              <ul className="gph-block-list gph-block-list--compact">
                {drill.supportMaterial.checklist.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            </div>
          )}
          {drill.supportMaterial?.tip && (
            <div className="gph-support-card">
              <span className="gph-support-card__label">Dica</span>
              <p>{drill.supportMaterial.tip}</p>
            </div>
          )}
          {drill.supportMaterial?.easierVariation && (
            <div className="gph-support-card">
              <span className="gph-support-card__label">Variação mais fácil</span>
              <p>{drill.supportMaterial.easierVariation}</p>
            </div>
          )}
          {drill.supportMaterial?.harderVariation && (
            <div className="gph-support-card">
              <span className="gph-support-card__label">Variação mais difícil</span>
              <p>{drill.supportMaterial.harderVariation}</p>
            </div>
          )}
          {complementary && (
            <div className="gph-support-card">
              <span className="gph-support-card__label">Treino complementar</span>
              <button
                type="button"
                className="gph-link-btn"
                onClick={() => agent.deliverDrill(complementary.drillId)}
              >
                {complementary.title}
              </button>
            </div>
          )}
        </div>
      </Section>

      <div className="gph-block-actions">
        <button
          type="button"
          className={`gph-btn ${isFavorite ? 'gph-btn--ghost' : 'gph-btn--primary'}`}
          onClick={() => agent.favorites.toggle(drill)}
        >
          <Star size={15} />
          {isFavorite ? 'Remover dos favoritos' : 'Favoritar treino'}
        </button>
        <button
          type="button"
          className="gph-btn gph-btn--ghost"
          onClick={() => agent.runCapability('mi_analysis_gameplay', { fromDrill: drill.drillId })}
        >
          <Video size={15} />
          Validar com vídeo
        </button>
        <button type="button" className="gph-btn gph-btn--ghost" onClick={() => agent.askAnotherDrill()}>
          Quero outra jogada
        </button>
      </div>
    </GphRichBlock>
  );
}
